import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { DataFlag, DataPoint, Graph, GraphPair } from 'src/app/app.types';
import { convertDateToMonth, months } from 'src/util/conversion-helpers';

@Component({
    selector: 'x-y-graph',
    templateUrl: './x-y-graph.component.html',
    styleUrls: ['./x-y-graph.component.scss'],
    standalone: false
})
export class XYGraphComponent implements OnChanges, OnInit {
    @Input() graphPair: GraphPair = {
      leftGraph: { description: null, type: 'line', color: '#39D6D6', data: [], labelType: 'normal' },
      rightGraph: { description: null, type: 'line', color: '#39D6D6', data: [], labelType: 'normal' },
      startDate: new Date(new Date().getFullYear()-1, new Date().getMonth() + 1, 1)
    };
    @Input() chartTitle = "X-Y Graph Title";
    @Input() chartId = "chart";
    /** Number of months on the x-axis, starting at graphPair.startDate. Defaults to a full year. */
    @Input() domainMonths = 12;
    /** Draws the dark highlight behind the current month's band. */
    @Input() highlightCurrentMonth = true;

    @Input() showKey = false;
    @Input() hasForecast = false;
    keys = [];

    @Input() stretchToFit = false;
    @Input() widthHeightRatio: number | null = null;
    @Input() flagMap: Map<number, DataFlag> = new Map<number, DataFlag>();
    // @Input() lineChartData: DataPoint[] = [
    //     { name: "Aug", actual: 85},
    //     { name: "Sep", actual: 155},//, predicted: 0 },
    //     { name: "Oct", actual: 235},//, predicted: 0 },
    //     { name: "Nov", actual: 330 },
    //     { name: "Dec", actual: 435},//, predicted: 0 },
    //     { name: "Jan", actual: 545 },
    //     { name: "Feb", actual: 680},//, predicted: 0 },
    //     { name: "Mar", actual: 820},//, predicted: 0 },
    //     { name: "Apr", actual: 940},//, predicted: 0 },
    //     { name: "May", actual: 1040 },
    //     { name: "June", actual: 1090 },
    //     { name: "July", predicted: 1250 },
    // ];
    @ViewChild("chartContainer", { static: true }) chartContainer!: ElementRef;

    private chartWidth = 0;
    private chartHeight = 365;
    @Input() highlightRange: { start; end };// = { start: "C", end: "E" };
    @Input() highlightAreaLinearGradient: { start: string; end: string };// = {
    //     start: "rgba(91, 254, 254, 0.28)",
    //     end: "rgba(17, 130, 130, 0.16)",
    // };

    //BAR
    // @Input() barChartData: DataPoint[] = [
    //     { name: "Aug", actual: 85 },
    //     { name: "Sep", actual: 70 },
    //     { name: "Oct", actual: 80 },
    //     { name: "Nov", actual: 95 },
    //     { name: "Dec", actual: 105 },
    //     { name: "Jan", actual: 110 },
    //     { name: "Feb", actual: 135 },
    //     { name: "Mar", actual: 140 },
    //     { name: "Apr", actual: 120 },
    //     { name: "May", actual: 100 },
    //     { name: "June", actual: 50 },
    //     { name: "July", actual: 61, predicted: 50 },
    // ];

    constructor(private hostElement: ElementRef) {}

    // ngAfterViewInit(): void {
    //     window.addEventListener("resize", this.onResize.bind(this));
    // }

    ngOnInit(): void {
        this.setChartDimensions();
        this.createCharts();
    }

    ngOnChanges(changes: SimpleChanges): void {
      console.log('Changes detected in XYGraphComponent for title :', this.chartTitle, changes);
        this.setChartDimensions();
        this.createCharts();
        this.keys = [
            { name: this.graphPair.leftGraph.description, color: this.graphPair.leftGraph.color }
        ]

        if(this.graphPair.rightGraph.description){
            this.keys.push({ name: this.graphPair.rightGraph.description, color: this.graphPair.rightGraph.color })
        }
    }

    private createCharts(): void {
        const margin = { top: 20, right: 30, bottom: 30, left: 40 };
        const width = this.chartWidth - margin.left - margin.right;
        const height = this.chartHeight - margin.top - margin.bottom;
        
        d3.select(`#${this.chartId}svg`).remove();
        
        const svg = d3
            .select(`#${this.chartId}`)
            .append("svg")
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .attr("id", `${this.chartId}svg`)
            .style("overflow", "visible")
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const combinedDomain = this.createDomain();
        console.log('Combined Domain:', combinedDomain);

        const xBand = d3
            .scaleBand()
            .domain(combinedDomain)
            .range([0, width])
            .padding(0.1);

        const leftYBand = d3
            .scaleLinear()
            .domain([0, d3.max(this.graphPair.leftGraph.data, (d) => (d.actual ?? 0) + (d.predicted ?? 0))!])
            .nice()
            .range([height, 0]);

        const rightYBand = d3
            .scaleLinear()
            .domain([0, d3.max(this.graphPair.rightGraph.data, (d) => (d.actual ?? 0) + (d.predicted ?? 0))!])
            .nice()
            .range([height, 0]);

        if (this.graphPair.leftGraph.type === 'bar') {
            this.createBarChart(xBand, leftYBand, svg, this.graphPair.leftGraph, height, "left");
        } else {
            this.createLineChart(xBand, leftYBand, svg, this.graphPair.leftGraph, height, "left");
        }

        console.log('Right Graph:', this.graphPair.rightGraph);
        console.log('Left Graph:', this.graphPair.leftGraph);

        if (this.graphPair.rightGraph.type === 'bar') {
            this.createBarChart(xBand, rightYBand, svg, this.graphPair.rightGraph, height, "right");
        } else {
            this.createLineChart(xBand, rightYBand, svg, this.graphPair.rightGraph, height, "right");
        }

        //sort axes

        // Left
        svg.append("g").attr("class", "y-axis-left").call(
            d3
                .axisLeft(leftYBand)
                .ticks(8)
                .tickFormat((d) =>
                    typeof d === "number" && Number.isInteger(d)
                        ? d.toLocaleString()
                        : ""
                )
                .tickSize(0) // removes tick lines
        );
        svg.selectAll(".y-axis-left .tick text")
            .attr("transform", `translate(-10, 0)`)
            .style("fill", "#999999")
            .style("font-family", "Segoe UI")
            .style("font-size", "12px");

        svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(xBand).tickSize(0)) // Remove tick lines
            .call(d3.axisBottom(xBand).tickPadding(3))
            .call((g) => g.selectAll(".x-axis .tick line").remove()) // Remove tick lines if needed
            .selectAll("path, line") // Target path (axis line) and ticks
            .attr("stroke", "#4A4A4A") // Change axis line and tick color
            .attr("stroke-width", 1);

        const flagMap = this.flagMap;
        const graphMarkerTooltip = this.createTooltip();

        // Customize x-axis labels to be multi-line if they contain spaces
        // Add in flags based on the flagMap which uses the month index as the key
        svg.selectAll(".x-axis .tick")
            .call(tickEles => tickEles.each(function(text, i) {
                const ele = d3.select(this);
                const flag = flagMap.get(i);
                const textEle = ele.select("text");
                const s = textEle.text().split(' '); 

                if(flag){
                    ele.append("line")
                        .attr("x1", `${flag.showLeftOfLabel ? `-` : ``}1.8em`)
                        .attr("y1","0.5em")
                        .attr("x2", `${flag.showLeftOfLabel ? `-` : ``}1.8em`)
                        .attr("y2",`${s.length > 1 ? "4" : "2.5"}em`)
                        .attr("style", `stroke:${flag.color};stroke-width:3`);
                    ele.style("pointer-events", "bounding-box")
                        .style("cursor", "default");
                    ele.on("mouseover", (event, d) => {
                        graphMarkerTooltip
                            .style("display", "block")
                            .html(
                                `<div class="center" style="flex-direction:column"><p style="margin:0; font-size:12px; display: flex; align-items: center; gap: 3px;"><img src="assets/icons/${flag.icon}.svg">${flag.tooltip}</p></div>`
                            )
                            .style("left", `${event.pageX + 10}px`)
                            .style("top", `${event.pageY - 20}px`);
                    })
                    .on("mouseout", (event) => {
                        d3.select(event.target).transition().duration(200).attr("r", 4); // Reset circle size

                        graphMarkerTooltip.style("display", "none");
                    });
                    textEle.style("fill", flag.color);
                }

                if(s.length > 1){
                    textEle.text('');
                    textEle.append("tspan")
                        .attr("x", 0)
                        .attr("dy",".71em")
                        .text(s[0]);
                    textEle.append("tspan")
                        .attr("x", 0)
                        .attr("dy","1.4em")
                        .text(s[1]);
                }
            })
            )
        
        d3.select(`#${this.chartId} .y-axis-left g:first-of-type.tick`).remove();
        d3.select(`#${this.chartId} .y-axis-left .domain`).attr("stroke", "#4A4A4A");
          
        // Right
        svg.append("g")
            .attr("class", "y-axis-right")
            .attr("transform", `translate(${width},0)`)
            .call(
                d3.axisRight(rightYBand)
                    .ticks(8)
                    .tickFormat((d) =>
                        Number.isInteger(d as number) ? d.toString() : ""
                    )
                    .tickSize(0)
            )
            .call((g) => {
                // Remove tick lines
                g.selectAll(".tick line").remove();

                // Style axis path
                g.select(".domain")
                    .attr("stroke", "#4A4A4A")
                    .attr("stroke-width", 1);

                // Optional: if any line elements are left to style (like grid lines)
                g.selectAll("line")
                    .attr("stroke", "#4A4A4A")
                    .attr("stroke-width", 1);

                // Adjust tick text position
                g.selectAll(".tick text").attr("dx", "-0.45em");
                
                g.selectAll("path, line").remove();
            });

        // Set font size of tick labels
        svg.selectAll(".tick text").style("font-size", "12px").style("color", this.graphPair.rightGraph.color);

        d3.select(`#${this.chartId} .y-axis-right g:first-of-type.tick`).remove();

        this.addLabels(svg, xBand, leftYBand, rightYBand, height);
        
        const currentDate = new Date();

        let currentMonth = months[currentDate.getMonth()];
        currentMonth = currentMonth === 'Jun' ? 'June' : currentMonth === 'Jul' ? 'July' : currentMonth;

        const endDate = new Date(this.graphPair.startDate.getFullYear() + 1, this.graphPair.startDate.getMonth(), 1);

        const xStart = xBand(currentMonth)!;
        
        if(this.highlightCurrentMonth && this.graphPair.startDate.getTime() < currentDate.getTime() && endDate.getTime() > currentDate.getTime() && xStart !== undefined){
            const xStartPos = xStart;
            const xEnd = xStartPos + xBand.bandwidth();
            const highlightWidth = xEnd - xStartPos;
            const rectWidth = highlightWidth;
            const rectHeight = height + 20;
            const radius = 4;

            const highlightedDataPoint = this.graphPair.leftGraph.data.find(dp => dp.name === currentMonth);

            const topPos = this.calculateDataPointYPosition(leftYBand, highlightedDataPoint!, height) - 30;

            const pathData = `
                M${xStartPos},${rectHeight} 
                L${xStartPos},${topPos + radius} 
                Q${xStartPos},${topPos} ${xStartPos + radius},${topPos} 
                L${xStartPos + rectWidth - radius},${topPos} 
                Q${xStartPos + rectWidth},${topPos} ${xStartPos + rectWidth},${topPos + radius} 
                L${xStartPos + rectWidth},${rectHeight} 
                Q${xStartPos + rectWidth},${rectHeight + radius} ${xStartPos + rectWidth - radius},${rectHeight + radius} 
                L${xStartPos + radius},${rectHeight + radius} 
                Q${xStartPos},${rectHeight + radius} ${xStartPos},${rectHeight} 
                Z
            `;

            svg.append("path")
                .attr("d", pathData)
                .attr("fill", "#3D3D3D")    
                .attr("stroke", "#605F61")
                .attr("stroke-width", 1)
                .lower();
        }
    }

    private setChartDimensions(): void {
        const parentElement = this.hostElement.nativeElement.parentElement;
        if (parentElement) {
            this.chartWidth = parentElement.offsetWidth;
            this.chartHeight = this.stretchToFit ? parentElement.offsetHeight*0.8 : this.chartHeight;
            if (this.widthHeightRatio) {
                this.chartHeight = this.chartWidth * this.widthHeightRatio;
            }
        }
    }

    createBarChart(xBand, yBand, svg, graph: Graph, height: number, classSuffix: string): void {
        // Bars
        svg.selectAll(`.bar-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => (d.actual ?? 0) > 0)
            .append("path")
            .attr("class", `bar-${classSuffix}`)
            .attr("d", (d) => {
                const xPos = xBand(d.name)! + xBand.bandwidth() / 4;
                const yPos = yBand(d.actual);
                const barWidth = xBand.bandwidth() / 2;
                const barHeight = height - yPos;
                // Clamp the corner radius to the bar's height so a short bar keeps tidy rounded top
                // corners instead of the rounded arc bulging below the x-axis. barWidth/2 leaves the
                // tall-bar domed top unchanged (the flat top segment then collapses to nothing).
                const radius = Math.min(barWidth / 2, barHeight);

                // Rounded top corners, square bottom sitting on the baseline.
                return `
                M${xPos},${height}
                L${xPos},${yPos + radius}
                Q${xPos},${yPos} ${xPos + radius},${yPos}
                L${xPos + barWidth - radius},${yPos}
                Q${xPos + barWidth},${yPos} ${xPos + barWidth},${yPos + radius}
                L${xPos + barWidth},${height}
                Z
            `;
            })
            .attr("fill", graph.color);

        this.addForecastedLines(svg, graph, height, xBand, yBand, classSuffix);
    }

    createLineChart(xBand, yBand, svg, graph: Graph, height: number, classSuffix: string): void {
        const splitLineData = this.splitIntoActualPredictedChunks(graph.data);
            
        // Get Line function
        const line = d3
            .line<{ x: string; y: number }>()
            .x((d) => xBand(d.name)! + xBand.bandwidth() / 2) // Center the line on the band
            .y((d) => this.calculateDataPointYPosition(yBand, d, height));

        console.log('Split Line Data:', splitLineData);

        for (const lineData of splitLineData) {
            // Draw line
            svg.append("path")
                .datum(lineData.data)
                .attr("fill", "none")
                .attr("stroke-dasharray", lineData.type === "predicted" ? "5 5" : "none")
                .attr("stroke", graph.color)
                .attr("stroke-width", 2)
                .attr("d", line);
        }

        this.addHoverOnLine(svg, graph, xBand, yBand, height, classSuffix);
    }

    addPointToTheLine(svg, x, y, pointX, pointY) {
        // Add the highlighted point
        svg.append("circle")
            .attr("cx", x(pointX)! + x.bandwidth() / 2) // Center of the band scale
            .attr("cy", y(pointY)) // Position based on y-value
            .attr("r", 8) // Outer ring radius
            .attr("fill", "#323232") // Outer ring color
            .attr("stroke", "#FCFCFC") // Middle ring color
            .attr("stroke-width", 1)
            .attr("class", "highlight-point");

        // Inner ring
        svg.append("circle")
            .attr("cx", x(pointX)! + x.bandwidth() / 2)
            .attr("cy", y(pointY))
            .attr("r", 4) // Inner ring radius
            .attr("fill", "#3FE8E8"); // Inner ring color
    }

    drawVerticalDashedLine(svg, x, y, pointY, pointX, height) {
        // Dashed line at the start of the highlighted area
        svg.append("line")
            .attr("x1", x(pointX)! + x.bandwidth() / 2) // x-coordinate for start
            .attr("x2", x(pointX)! + x.bandwidth() / 2)
            .attr("y1", y(pointY)) // Top of the chart
            .attr("y2", height) // Bottom of the chart
            .attr("stroke", "#9A9999") // Line color
            .attr("stroke-dasharray", "4,4") // Dashed line style
            .attr("stroke-width", 1);
    }

    addHoverOnLine(svg, graph: Graph, x, y, height, classSuffix: string) {
        console.log(`current graph data for hover: `, graph.data);
        // Draw circles for hover effect
        const tooltip = this.createTooltip();

        svg.selectAll(`.dot-${classSuffix}`)
            .data(graph.data)
            .enter()
            .append("circle")
            .attr("class", `dot-${classSuffix}`)
            .attr("cx", (d) => x(d.name)! + x.bandwidth() / 2)
            .attr("cy", (d) => {
                console.log(`chartId ${this.chartId} DataPoint ${d.name}: yPosition=${this.calculateDataPointYPosition(y, d, height)}`);
                return this.calculateDataPointYPosition(y, d, height)
            })
            .attr("r", 4) // Circle size
            .attr("fill", "#323232")
            .attr("stroke", "#FFFFFF")
            .style("cursor", "pointer")
            .on("mouseover", (event, d) => {
                if(graph.labelType === 'normal') {return;}
                d3.select(event.target).transition().duration(200).attr("r", 6); // Enlarge circle on hover

                const total = (d.actual ?? 0) + (d.predicted ?? 0);
                console.log(`Hovering over ${d.name}: total=${total}, actual=${d.actual}, predicted=${d.predicted}`);

                tooltip
                    .style("display", "block")
                    .html(
                        `<div class="center" style="flex-direction:column"><p style="margin:0; font-size:12px">${d.name}</p><p style="margin:0; font-size:14px">${total}</p></div>`
                    )
                    .style("left", `${event.pageX + 10}px`)
                    .style("top", `${event.pageY - 20}px`);
            })
            .on("mouseout", (event) => {
                if(graph.labelType === 'normal') {return;}
                d3.select(event.target).transition().duration(200).attr("r", 4); // Reset circle size

                tooltip.style("display", "none");
            });

        svg.selectAll(`.inner-dot-${classSuffix}`)
            .data(graph.data)
            .enter()
            .append("circle")
            .attr("class", `inner-dot-${classSuffix}`)
            .attr("cx", (d) => x(d.name)! + x.bandwidth() / 2)
            .attr("cy", (d) => this.calculateDataPointYPosition(y, d, height))
            .attr("r", 2) // Circle size
            .attr("fill", graph.color)
            .style("cursor", "pointer")
            .style("pointer-events", "none");
    }

    createTooltip = () => d3
            .select(`#${this.chartId}`)
            .append("div")
            .style("position", "absolute")
            .style("background", "#00000070")
            .style("border", "1px solid #ccc")
            .style("padding", "5px")
            .style("border-radius", "5px")
            .style("display", "none")
            .style("pointer-events", "none")
            .style("font-size", "12px")
            .style("box-shadow", "0px 2px 5px rgba(0, 0, 0, 0.2)");

    addForecastedLines(svg, graph: Graph, height, xBar, yBar, classSuffix?: string): void {

        // Forecasted Lines
        svg.selectAll(`.forecasted-line-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => d.predicted > 0)
            .append("path")
            .attr("class", `forecasted-line-${classSuffix}`)
            .attr("d", (d) => {
                const forecastDotBuffer = 6;
                const actualDataLabelBuffer = 20;

                const xPos = xBar(d.name)! + xBar.bandwidth() / 4;

                const offset = (yBar(d.actual) ?? height);
                const forecastedYPos = height - yBar(d.predicted);
                let lineStart = offset;

                if(offset !== height) {
                    lineStart = lineStart - actualDataLabelBuffer;
                }

                const barWidth = xBar.bandwidth() / 2;
                const radius = barWidth / 2;

                return `
                M${xPos+radius},${lineStart} 
                L${xPos+radius},${offset - forecastedYPos + forecastDotBuffer} 
                Z
            `;
            })
            .attr("stroke", graph.color)
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "5 5");

        // Forecasted Dots
        svg.selectAll(`.forecasted-dot-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => d.predicted > 0)
            .append("circle")
            .attr("class", `forecasted-dot-${classSuffix}`)
            .attr("cx", (d) => xBar(d.name)! + xBar.bandwidth() / 2)
            .attr("cy", (d) => this.calculateDataPointYPosition(yBar, d, height))
            .attr("r", 4) // Circle size
            .attr("fill", "#323232")
            .attr("stroke", "#FFFFFF");

        // Forecasted Inner Dots
        svg.selectAll(`.forecasted-inner-dot-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => d.predicted > 0)
            .append("circle")
            .attr("class", `forecasted-inner-dot-${classSuffix}`)
            .attr("cx", (d) => xBar(d.name)! + xBar.bandwidth() / 2)
            .attr("cy", (d) => this.calculateDataPointYPosition(yBar, d, height))
            .attr("r", 2) // Circle size
            .attr("fill", graph.color);
    }

    splitIntoActualPredictedChunks(combinedLineData: DataPoint[]): { data: DataPoint[], type: "predicted" | "actual" }[] {
        const res = [];
        let currentChunk: DataPoint[] = [];
        let currentType: "predicted" | "actual" | null = null;
        let previousType: "predicted" | "actual" | null = null;
        let previousPoint: DataPoint | null = null;

        for (const point of combinedLineData) {
            if(currentChunk.length) {
                currentType = point.predicted !== undefined ? "predicted" : "actual";
            } else {
                currentType = null;
            }
            
            if(previousType && currentType !== previousType) {
                res.push({ data: currentChunk, type: previousType });
                currentChunk = [previousPoint, point];
            } else {
                currentChunk.push(point);
            }
            previousType = currentType;
            previousPoint = point;
        }

        if (!res.includes(currentChunk)){
            res.push({ data: currentChunk, type: currentType! });
        }

        return res;
    }

    calculateDataPointYPosition(yScale, dataPoint: DataPoint, height: number): number {
        const actualAbs = height - yScale(dataPoint.actual ?? 0);
        const predictedAbs = height - yScale(dataPoint.predicted ?? 0);
        
        return height-(actualAbs+predictedAbs);
    }

    @HostListener("window:resize")
    private onResize(): void {
        this.setChartDimensions();
        this.createCharts();
    }

    private createDomain(): string[] {
        const combinedDomain = [];

        const currentDate = new Date(this.graphPair.startDate.getTime());

        // domainMonths defaults to 12, preserving the original full-year behaviour.
        for (let i = 0; i < this.domainMonths; i++) {
            combinedDomain.push(convertDateToMonth(currentDate));
            currentDate.setMonth(currentDate.getMonth() + 1);
        }

        return combinedDomain;
    }

    private addLabels(svg: any, xBand: any, yBandLeft: any,yBandRight: any, height: number): void {
        if(this.graphPair.leftGraph.type === 'bar' && this.graphPair.leftGraph.labelType === 'normal') {
            this.addBarLabels(svg, "left", this.graphPair.leftGraph, xBand, yBandLeft, height);
        } else if (this.graphPair.leftGraph.labelType === 'normal') {
            this.addLineLabels(svg, "left", this.graphPair.leftGraph, xBand, yBandLeft, height);
        }

        if(this.graphPair.rightGraph.type === 'bar' && this.graphPair.rightGraph.labelType === 'normal') {
            this.addBarLabels(svg, "right", this.graphPair.rightGraph, xBand, yBandRight, height);
        } else if (this.graphPair.rightGraph.labelType === 'normal') {
            this.addLineLabels(svg, "right", this.graphPair.rightGraph, xBand, yBandRight, height);
        }
    }

    private addBarLabels(svg: any, classSuffix: string, graph: Graph, xBand: any, yBand: any, height: number): void {
        // Bar Labels
        svg.selectAll(`.label-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => (d.actual ?? 0) > 0)
            .append("text")
            .attr("x", (d) => xBand(d.name)! + xBand.bandwidth() / 2)
            .attr("y", (d) => yBand(d.actual) - 5)
            .attr("text-anchor", "middle")
            .attr("fill", "#fcfcfc")
            .attr("font-size", "12px")
            .text((d) =>
                typeof d.actual === "number" ? d.actual.toLocaleString() : ""
            );

        // Forecasted Labels Bar
        svg.selectAll(`.forecasted-label-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => d.predicted > 0)
            .append("text")
            .attr("x", (d) => xBand(d.name)! + xBand.bandwidth() / 2)
            .attr("y", (d) => {
                const dotBuffer = 4; 
                const labelBuffer = 5; 

                return this.calculateDataPointYPosition(yBand, d, height) - labelBuffer - dotBuffer;
            })
            .attr("text-anchor", "middle")
            .attr("fill", "#fcfcfc")
            .attr("font-size", "12px")
            .text((d) => {
                return typeof d.predicted === "number" && typeof (d.actual ?? 0) === "number" ? 
                    ((d.actual ?? 0) + d.predicted).toLocaleString() : ""
                });
    }

    private addLineLabels(svg: any, classSuffix: string, graph: Graph, xBand: any, yBand: any, height: number): void {
        // Line Labels
        svg.selectAll(`.label-${classSuffix}`)
            .data(graph.data)
            .enter()
            .filter(d => (d.actual ?? 0) + (d.predicted ?? 0) > 0)
            .append("text")
            .attr("x", (d) => xBand(d.name)! + xBand.bandwidth() / 2)
            .attr("y", (d) => this.calculateDataPointYPosition(yBand, d, height) - 10)
            .attr("text-anchor", "middle")
            .attr("fill", "#fcfcfc")
            .attr("font-size", "12px")
            .text((d) =>((d.actual ?? 0) + (d.predicted ?? 0)).toLocaleString());
    }
}
